import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as s from './client.scss';
import i18n from '../../../i18n/client-i18n';
import { I18nextProvider } from 'react-i18next';
import { InvoiceViewV2 } from '@wix/price-quote-view';
import { InvoiceActions } from './InvoiceActions/InvoiceActions';
import webBiLogger from '@wix/web-bi-logger';
import dashBiLogger, {
  quotesViewInvoicePageViewParams,
  quotesDownloadPdfParams,
  quotesPrintInvoice477Params,
  quotesPayInvoiceClick478Params,
} from '@wix/bi-logger-dash';
import { InvoiceStatus } from '@wix/ambassador-wix-quotes-web/types';
import { PaymentType } from '../../../constants/bi';
import { extractBusinessCustomFields, extractDocumentCustomFields } from '../../../api/mapCustomFields';

const factory = webBiLogger.factory();
const logger = dashBiLogger(factory)();

interface InvoiceViewClientProps {
  invoice: any;
  businessInfo: any;
  status: InvoiceStatus;
  ascend: boolean;
  isUoU: boolean;
  invoiceId: string;
  instanceId: string;
  invoiceVersion: number;
  token: string;
  hasPaymentMethods: boolean;
  recurringInvoiceViewEnabled: boolean;
  data: { msid: string; experiments: any };
  footer: { notesLabel: string, legalTermsLabel: string };
  invoiceNumberLabel: string;
}

class InvoiceViewClient extends React.Component<InvoiceViewClientProps> {
  componentDidMount() {
    const {
      invoice: {
        customer: { contactId: uuid },
        metaData: { source },
      },
      status,
      invoiceId: invoice_id,
      hasPaymentMethods,
      recurringInvoiceViewEnabled,
      data: { msid },
    } = this.props;
    const invoiceViewBiPayload: quotesViewInvoicePageViewParams = {
      invoice_id,
      paid: status === 'Paid',
      origin: 'customer',
      is_visible: hasPaymentMethods,
      paymentType: recurringInvoiceViewEnabled ? PaymentType.RECURRING : PaymentType.ONE_TIME,
      invoiceSource: source,
      msid
    };

    invoiceViewBiPayload['uuid'] = uuid;

    logger.quotesViewInvoicePageView(invoiceViewBiPayload);
  }

  private reportPdfDownloadBI(paymentType: PaymentType) {
    const {
      invoice: {
        customer: { contactId: uuid },
      },
      invoiceId: invoice_id,
    } = this.props;
    const downloadPdfBiPayload: quotesDownloadPdfParams = {
      invoice_id,
      origin: 'customer',
      paymentType,
    };

    downloadPdfBiPayload['uuid'] = uuid;

    logger.quotesDownloadPdf(downloadPdfBiPayload);
  }

  private reportPrintBI(paymentType: PaymentType) {
    const { invoiceId: invoice_id } = this.props;
    const printBiPayload: quotesPrintInvoice477Params = {
      invoice_id,
      origin: 'customer',
      paymentType,
    };

    logger.quotesPrintInvoice477(printBiPayload);
  }

  reportPayBI = async (paymentType: PaymentType) => {
    const { invoiceId: invoice_id, invoice } = this.props;
    const printBiPayload: quotesPayInvoiceClick478Params = {
      invoice_id,
      invoiceSource: invoice.metaData.source,
      externalId: invoice.metaData.sourceRefId,
      origin: 'customer',
      paymentType,
      totalAmountLong: invoice.totals && invoice.totals.total && invoice.totals.total.serializedValue * 100000
    };

    await logger.quotesPayInvoiceClick478(printBiPayload);
  };

  render() {
    const {
      invoice,
      businessInfo,
      status,
      ascend,
      instanceId,
      invoiceId,
      token,
      invoiceVersion,
      hasPaymentMethods,
      recurringInvoiceViewEnabled,
      isUoU,
      footer,
      invoiceNumberLabel,
      data: { experiments },
    } = this.props;

    const urlPrefix = window['__URL_PREFIX__'];
    const consentPolicyEnabled = window['__CONSENT_POLICY_ENABLED__'];
    const hasBalance = invoice.dynamicTotals.balance && !!invoice.dynamicTotals.balance.serializedValue;
    const pdfUrl = `${consentPolicyEnabled ? urlPrefix : ''}/invoice/${instanceId}:${invoiceId}/pdf/view`;
    const payUrl = `${consentPolicyEnabled ? urlPrefix : ''}/invoice/pay/${instanceId}:${invoiceId}`;
    const payRecurringUrl = `${consentPolicyEnabled ? urlPrefix : ''}/invoice/pay/recurring/${instanceId}:${invoiceId}`;
    const withPay = (hasPaymentMethods && !['Paid', 'Void', 'Processing'].includes(status)) && hasBalance;
    const paymentType = recurringInvoiceViewEnabled ? PaymentType.RECURRING : PaymentType.ONE_TIME;
    return (
      <div data-hook="invoice-view-client" className={s.container} style={window['__INITIAL_I18N__'].locale === 'ja' ? { "--wsr-font-family": "Noto Sans CJK JP Regular" } as any : {}}>
        <div className={s.actionsHeader}>
          {
            <InvoiceActions
              withPay={withPay}
              pdfUrl={pdfUrl}
              payUrl={recurringInvoiceViewEnabled ? payRecurringUrl : payUrl}
              token={token}
              print={() => {
                this.reportPrintBI(paymentType);
                window.print();
              }}
              version={invoiceVersion}
              onDownloadPDF={() => this.reportPdfDownloadBI(paymentType)}
              onPayForm={async () => {
                await this.reportPayBI(paymentType);
              }}
              experiments={experiments}
            />
          }
        </div>
        <div className={s.content}>
          <InvoiceViewV2
            recurringInvoiceViewEnabled={recurringInvoiceViewEnabled}
            recurringStandaloneViewEnabled={true}
            invoice={invoice}
            businessInfo={businessInfo}
            status={status}
            isUoU={isUoU}
            ascend={ascend}
            documentCustomFields={extractDocumentCustomFields(invoice.customFields || [])}
            businessCustomFields={extractBusinessCustomFields(invoice.customFields || [])}
            footer={footer}
            customInvoiceNumberLabel={invoiceNumberLabel}
            customFields={invoice.customFields || []}
          />
        </div>
      </div>
    );
  }
}

ReactDOM.render(
  <I18nextProvider i18n={i18n(window['__INITIAL_I18N__'])}>
    <InvoiceViewClient
      invoice={window['__INVOICE__']}
      recurringInvoiceViewEnabled={window['__RECURRING_INVOICE_VIEW_ENABLED__']}
      status={window['__STATUS__']}
      businessInfo={window['__BUSINESS_INFO__']}
      ascend={window['__ASCEND__']}
      invoiceId={window['__INVOICE_ID__']}
      invoiceVersion={window['__INVOICE_VERSION__']}
      instanceId={window['__INSTANCE_ID__']}
      token={window['__TOKEN__']}
      isUoU={!!window['__TOKEN__']}
      hasPaymentMethods={window['__HAS_PAYMENT_METHODS__']}
      data={window['__DATA__']}
      footer={window['__FOOTER_LABELS__']}
      invoiceNumberLabel={window['__INVOICE_NUMBER_LABEL__']}
    />
  </I18nextProvider>,
  document.getElementById('root')
);
